import { create } from 'zustand';
import { message } from 'antd';
const Konva = require('konva');
import {
  getCoordsByAttachMode,
  getAttachedSourcePosition,
  calculatePosition,
  isParentGroupElement,
} from '../services/AttachedElementsService';
import useStageStore from './useStageStore';
import usePropertiesStore from './usePropertiesStore';
import {
  paintAttachClusterLinkIconColor,
  setAttachLinkIconColorAttr,
} from '../utils/attachLayerPartners';

const getStage = () => useStageStore.getState().stage;
const getRefreshProperties = () => usePropertiesStore.getState().refreshProperties;
const getSetSelectedElementPosition = () => useStageStore.getState().setSelectedElementPosition;

function getClientRectRelativeTo(node, ancestor) {
  const inv = ancestor.getAbsoluteTransform().copy();
  inv.invert();
  const box = node.getClientRect({ skipTransform: false });
  const corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.width, y: box.y },
    { x: box.x + box.width, y: box.y + box.height },
    { x: box.x, y: box.y + box.height },
  ].map((c) => inv.point(c));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function getUnionBBoxCenterLocal(group, visible) {
  const relRects = visible.map((c) => getClientRectRelativeTo(c, group));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  relRects.forEach((r) => {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  });
  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

function getCanvasCenterInGroupLocal(group, stage) {
  const bg = stage?.findOne?.('#canvas_bg');
  if (!bg || !group) return null;
  const r = getClientRectRelativeTo(bg, group);
  if (!r.width && !r.height) return null;
  return {
    cx: r.x + r.width / 2,
    cy: r.y + r.height / 2,
  };
}

function distributeGroupFromUnionCenter(group, visible, align, offset) {
  const o = Number(offset) || 0;
  const axis = String(align) === '2' ? 'vertical' : 'horizontal';
  if (!visible.length) return;

  const stage = group.getStage();
  const canvasCenter = getCanvasCenterInGroupLocal(group, stage);
  const fallback = getUnionBBoxCenterLocal(group, visible);
  const { cx, cy } = canvasCenter ?? fallback;
  const relRects = visible.map((c) => getClientRectRelativeTo(c, group));

  const curCenter = (i) => ({
    x: relRects[i].x + relRects[i].width / 2,
    y: relRects[i].y + relRects[i].height / 2,
  });

  if (visible.length === 1) {
    const c = curCenter(0);
    visible[0].x(visible[0].x() + (cx - c.x));
    visible[0].y(visible[0].y() + (cy - c.y));
    group.getLayer()?.batchDraw();
    return;
  }

  if (axis === 'horizontal') {
    const widths = relRects.map((r) => r.width);
    const totalWidth = widths.reduce((a, b) => a + b, 0) + o * (visible.length - 1);
    let xCenter = cx - totalWidth / 2 + widths[0] / 2;
    const xCenters = [];
    for (let i = 0; i < visible.length; i++) {
      xCenters.push(xCenter);
      if (i < visible.length - 1) {
        xCenter += widths[i] / 2 + o + widths[i + 1] / 2;
      }
    }
    for (let i = 0; i < visible.length; i++) {
      const c = curCenter(i);
      visible[i].x(visible[i].x() + (xCenters[i] - c.x));
      visible[i].y(visible[i].y() + (cy - c.y));
    }
  } else {
    const heights = relRects.map((r) => r.height);
    const totalHeight = heights.reduce((a, b) => a + b, 0) + o * (visible.length - 1);
    let yCenter = cy - totalHeight / 2 + heights[0] / 2;
    const yCenters = [];
    for (let i = 0; i < visible.length; i++) {
      yCenters.push(yCenter);
      if (i < visible.length - 1) {
        yCenter += heights[i] / 2 + o + heights[i + 1] / 2;
      }
    }
    for (let i = 0; i < visible.length; i++) {
      const c = curCenter(i);
      visible[i].x(visible[i].x() + (cx - c.x));
      visible[i].y(visible[i].y() + (yCenters[i] - c.y));
    }
  }


  group.getLayer()?.batchDraw();
  const after = getUnionBBoxCenterLocal(group, visible);
  const fixX = cx - after.cx;
  const fixY = cy - after.cy;
  if (Math.abs(fixX) > 1e-6 || Math.abs(fixY) > 1e-6) {
    for (const node of visible) {
      node.x(node.x() + fixX);
      node.y(node.y() + fixY);
    }
  }
  group.getLayer()?.batchDraw();
}

const useAttachedElementsStore = create((set, get) => ({
  getCoordsByAttachMode,
  getAttachedSourcePosition,

  moveAttachedSources: async (target, visited = new Set()) => {
    try {
      if (!target?.getStage?.() || !target?.getAttr) return;
      // Guard against cyclic / self-referential attach clusters. Without this a
      // node that is both source and target of the same attachId (attachId === one
      // of its own attachIds) recurses forever, compounding getAttachedSourcePosition
      // offsets until the position explodes off-canvas (observed ~2_000_000).
      if (visited.has(target._id)) return;
      visited.add(target._id);

      const currentStage = target.getStage();
      const attachIds = target.getAttr('attachIds');
      if (!attachIds) return;

      for (const attachId of attachIds) {
        if (!target?.getStage?.() || !currentStage?.getStage?.()) return;
        const source = currentStage
          .find('.source')
          .find((e) => e?.getAttr?.('attachId') === attachId);
        const scale = currentStage.scaleX();
        let newTargetPosition = calculatePosition(target, currentStage, scale);

        if (isParentGroupElement(target)) {
          const groupPosition = calculatePosition(target.parent, currentStage, scale);
          newTargetPosition = {
            x: newTargetPosition.x - groupPosition.x,
            y: newTargetPosition.y - groupPosition.y,
          };
        }
        target.position(newTargetPosition);

        // Skip a self-attached source (an element pinned to itself): repositioning
        // it relative to itself just accumulates an offset every pass.
        if (source && source._id !== target._id) {
          const newSourcePosition = getAttachedSourcePosition(
            target,
            source.getAttr('attachMode'),
            source,
            source.getAttr('sourceAttachMode'),
            source.getAttr('attachX'),
            source.getAttr('attachY'),
          );
          if (newSourcePosition) {
            source.position(newSourcePosition);
          }
          if (source.hasName('target') && !visited.has(source._id)) {
            await get().moveAttachedSources(source, visited);
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateSourceAttachedOffset: (source) => {
    const currentStage = source.getStage();
    const attachId = source.getAttr('attachId');
    const attachedTarget = currentStage.find('.target').find(
      (e) => e.getAttr('attachIds')?.indexOf(attachId) >= 0,
    );
    const newPosition = getAttachedSourcePosition(
      attachedTarget,
      source.getAttr('attachMode'),
      source,
      source.getAttr('sourceAttachMode'),
      source.getAttr('attachX'),
      source.getAttr('attachY'),
    );
    if (newPosition) {
      source.setAttr('attachX', source.x() - newPosition.x);
      source.setAttr('attachY', source.y() - newPosition.y);
    }
  },

  detachElement: (source) => {
    const stage = getStage();
    if (!source.hasName('source') || !stage) return;
    const attachId = source.getAttr('attachId');
    const attachedTarget = stage.find('.target').find(
      (e) => e.getAttr('attachIds')?.indexOf(attachId) >= 0,
    );
    if (!attachedTarget) return;

    setAttachLinkIconColorAttr(source, '');

    source.removeName('source');
    source.setAttr('attachId', undefined);
    source.setAttr('attachMode', undefined);
    source.setAttr('attachX', undefined);
    source.setAttr('attachY', undefined);
    if (attachedTarget.getAttr('attachIds').length > 1) {
      const ids = attachedTarget.getAttr('attachIds').filter((t) => t !== attachId);
      attachedTarget.setAttr('attachIds', ids);
    } else {
      attachedTarget.removeName('target');
      attachedTarget.setAttr('attachIds', undefined);
      setAttachLinkIconColorAttr(attachedTarget, '');
    }
    getRefreshProperties()();
  },

  attachToElement: (source, target, targetMode = '3', sourceMode = '1', offsetX = 0, offsetY = 0) => {
    const stage = getStage();
    if (!stage) return;
    if (!source || !target || source === target || source._id === target._id) {
      message.error('Cannot attach an element to itself.');
      return false;
    }
    get().detachElement(source);

    const noLoop = (checkId, currentTarget) => {
      if (currentTarget._id === checkId) return false;
      if (currentTarget.hasName('source')) {
        const attachedTarget = stage.find('.target').find(
          (e) => e.getAttr('attachIds').indexOf(currentTarget.getAttr('attachId')) >= 0,
        );
        return noLoop(checkId, attachedTarget);
      }
      return true;
    };

    if (source.getAttr('elementType') === 'group') {
      for (const child of source.children) {
        if (!noLoop(source._id, child)) {
          message.error('Loop detected.');
          return false;
        }
      }
    }
    if (!noLoop(source._id, target)) {
      message.error('Loop detected.');
      return false;
    }

    const attachId = stage.findOne('.source')
      ? Math.max(...stage.find('.source').map((el) => el.getAttr('attachId'))) + 1
      : 0;

    source.addName('source');
    source.setAttr('attachId', attachId);
    source.setAttr('attachMode', targetMode);
    source.setAttr('sourceAttachMode', sourceMode);
    source.setAttr('attachX', offsetX);
    source.setAttr('attachY', offsetY);

    if (target.getAttr('attachIds')) {
      const ids = target.getAttr('attachIds');
      ids.push(attachId);
      target.setAttr('attachIds', ids);
    } else {
      target.addName('target');
      target.setAttr('attachIds', [attachId]);
    }

    const newPosition = getAttachedSourcePosition(target, targetMode, source, sourceMode, offsetX, offsetY);
    source.position(newPosition);

    getSetSelectedElementPosition()({ x: target.x(), y: target.y() });
    paintAttachClusterLinkIconColor(stage, target);
    getRefreshProperties()();
  },

  updateAttachedPosition: (source, offsetX, offsetY, onComplete) => {
    if (!source.hasName('source')) return;
    const currentStage = source.getStage();
    if (!currentStage) return;
    const attachId = source.getAttr('attachId');
    const targets = currentStage.find('.target');
    const attachedTarget = targets
      ? Array.from(targets).find((e) => e.getAttr('attachIds')?.indexOf(attachId) >= 0)
      : undefined;
    if (!attachedTarget) return;

    const newPosition = getAttachedSourcePosition(
      attachedTarget,
      source.getAttr('attachMode'),
      source,
      source.getAttr('sourceAttachMode'),
      offsetX,
      offsetY,
    );
    if (newPosition) {
      source.position(newPosition);
      source.setAttr('attachX', offsetX);
      source.setAttr('attachY', offsetY);
    }
    onComplete?.();
  },

  distributeGroup: (group, direction = 'left', align = '1', offset = 0, createGroupFn) => {
    const stage = getStage();
    if (!stage) return;
    let targetGroup = group;
    if (targetGroup instanceof Konva.Transformer) {
      targetGroup = createGroupFn?.();
    }
    if (!targetGroup) {
      message.error('Nothing to distribute');
      return;
    }
    if (!targetGroup?.getStage?.() || !Array.isArray(targetGroup?.children)) return;

    if (!targetGroup.hasName('distributed')) targetGroup.addName('distributed');
    targetGroup.setAttr('distributeDirection', direction);
    targetGroup.setAttr('distributeAlign', align);
    targetGroup.setAttr('distributeOffset', offset);

    targetGroup.children.forEach((child) => child.hasName('source') && get().detachElement(child));
    const visible = targetGroup.children.filter((child) => child.getAttr('invisible') !== true);
    visible.sort((a, b) => a.zIndex() - b.zIndex());

    if (direction === 'center') {
      distributeGroupFromUnionCenter(targetGroup, visible, align, offset);
      const tr = stage.findOne('Transformer');
      if (tr) tr.nodes(tr.nodes());
      getRefreshProperties()();
      return;
    }

    const translationTarget = {
      left1: '3', left2: '6', left3: '9',
      right1: '1', right2: '4', right3: '7',
      up1: '7', up2: '8', up3: '9',
      down1: '1', down2: '2', down3: '3',
    };
    const translationSource = {
      left1: '1', left2: '4', left3: '7',
      right1: '3', right2: '6', right3: '9',
      up1: '1', up2: '2', up3: '3',
      down1: '7', down2: '8', down3: '9',
    };

    const modifyTargetElement = (grp, tgt, elements, dir, aln) => {
      if (!tgt || !elements?.length) return;
      const groupRect = grp.getClientRect();
      const targetRect = tgt.getClientRect();
      const firstElement = [...elements].sort((a, b) => a.zIndex() - b.zIndex())[0];
      let x = firstElement.x(), y = firstElement.y();

      if (dir === 'up' && aln === '1') { x = 0; y = 0; }
      else if (dir === 'up' && aln === '2') { x = (groupRect.width - targetRect.width) / 2; y = 0; }
      else if (dir === 'up' && aln === '3') { x = groupRect.width - targetRect.width; y = 0; }
      else if (dir === 'down' && aln === '1') { x = 0; y = groupRect.height - targetRect.height; }
      else if (dir === 'down' && aln === '2') { x = (groupRect.width - targetRect.width) / 2; y = groupRect.height - targetRect.height; }
      else if (dir === 'down' && aln === '3') { x = groupRect.width - targetRect.width; y = groupRect.height - targetRect.height; }
      else if (dir === 'left' && aln === '1') { x = 0; y = 0; }
      else if (dir === 'left' && aln === '2') { x = 0; y = (groupRect.height - targetRect.height) / 2; }
      else if (dir === 'left' && aln === '3') { x = 0; y = groupRect.height - targetRect.height; }
      else if (dir === 'right' && aln === '1') { x = groupRect.width - targetRect.width; y = 0; }
      else if (dir === 'right' && aln === '2') { x = groupRect.width - targetRect.width; y = (groupRect.height - targetRect.height) / 2; }
      else if (dir === 'right' && aln === '3') { x = groupRect.width - targetRect.width; y = groupRect.height - targetRect.height; }

      tgt.x(x);
      tgt.y(y);
    };

    let target = visible[0];
    modifyTargetElement(targetGroup, target, targetGroup.children, direction, align);

    for (const source of visible.slice(1)) {
      let oX = 0, oY = 0;
      if (direction === 'left') oX = offset;
      else if (direction === 'right') oX = -offset;
      else if (direction === 'up') oY = offset;
      else oY = -offset;
      get().attachToElement(source, target, translationTarget[direction + align], translationSource[direction + align], oX, oY);
      target = source;
    }
    const tr = stage.findOne('Transformer');
    if (tr) tr.nodes(tr.nodes());
    getRefreshProperties()();
  },

  reDistributeGroup: (group) => {
    if (!group?.getStage?.() || !group?.getAttr) return;
    const direction = group.getAttr('distributeDirection');
    const align = group.getAttr('distributeAlign');
    const offset = group.getAttr('distributeOffset');
    get().distributeGroup(group, direction, align, offset);
  },

  destroyDistributedGroup: (group) => {
    if (!group.hasName('distributed')) return;
    group.removeName('distributed');
    group.setAttr('distributeDirection', undefined);
    group.setAttr('distributeAlign', undefined);
    group.setAttr('distributeOffset', undefined);
    for (const el of group.children.filter((c) => c.hasName('source'))) {
      get().detachElement(el);
    }
  },
}));

export default useAttachedElementsStore;
